import { Buffer } from 'buffer'
import React, { createContext, useContext, useMemo, useState } from 'react'
import { v4 } from 'uuid'
import IenApi from '../api/IenApi'
import { toastError } from '../customs/Toast/Toast'
import { iUploadResponse } from '../types/submit.types'
import { cipherFile } from '../utils/cipher'
import { errorIenProces, errorIenProcesToast } from '../utils/errorIenProces'

export interface iUploadFile {
  id: string
  fileData: File
  signal: AbortController
  error?: string
  uploaded?: boolean
  response?: iUploadResponse
}

interface iFilesUploadInputList {[inputName: string]: iUploadFile[]}

interface iUploadFileConfig {
  extSystemId: string
  cipherCert?: string
}

interface iFormsUpload {
  files: iFilesUploadInputList
  progress: { [id: string]: number }
  uploadFile: (inputName: string, file: File) => void
  cancelFile: (inputName: string, id: string) => void
  deleteFile: (inputName: string, id: string) => void
  config: iUploadFileConfig
  updateConfig: (data: Partial<iUploadFileConfig>) => void
}

const FormUploadContext = createContext<iFormsUpload>({} as iFormsUpload)

const useFormUploadFilesBase = (inputName: string) => {
  const formUploadContext = useContext(FormUploadContext)
  const files = formUploadContext.files[inputName] || []
  return useMemo(() => ({
    filesUploaded: files.filter(file => file.uploaded),
    filesUploading: files.filter(file => !file.uploaded)
  }), [files])
}

export const useFormUploadInput = (inputName: string) => {
  const formUploadContext = useContext(FormUploadContext)
  const { filesUploaded, filesUploading } = useFormUploadFilesBase(inputName)
  return useMemo(() => ({
    uploadFile: (file: File) => formUploadContext.uploadFile(inputName, file),
    filesUploaded: filesUploaded,
    filesUploading: filesUploading,
  }), [filesUploaded, filesUploading])
}

export const useFormUploadFiles = (inputName: string) => {
  const formUploadContext = useContext(FormUploadContext)
  const { filesUploaded, filesUploading } = useFormUploadFilesBase(inputName)
  return useMemo(() => ({
    cancelFile: (id: string) => formUploadContext.cancelFile(inputName, id),
    deleteFile: (id: string) => formUploadContext.deleteFile(inputName, id),
    filesUploaded: filesUploaded,
    filesUploading: filesUploading
  }), [filesUploaded, filesUploading])
}

export const useFormUploadFilesProgress = () => {
  const formUploadContext = useContext(FormUploadContext)
  const progress = formUploadContext.progress
  return useMemo(() => ({
    progress
  }), [progress])
}

export const useFormUploadState = (inputName: string) => {
  const { filesUploaded, filesUploading } = useFormUploadFilesBase(inputName)
  return useMemo(() => ({
    isUploading: !!filesUploading.length,
    filesUploaded: filesUploaded,
  }), [filesUploaded, filesUploading])
}

export const useFormUploadConfig = () => {
  const formUploadContext = useContext(FormUploadContext)
  return useMemo(() => ({
    config: formUploadContext.config,
    updateConfig: formUploadContext.updateConfig
  }), [])
}

const FormUploadProvider = ({ children }: { children: React.ReactNode }) => {

  const [config, setConfig] = useState<iUploadFileConfig>({} as iUploadFileConfig)
  const { extSystemId, cipherCert } = config

  const [files, setFiles] = useState<iFilesUploadInputList>({})
  const [progress, setProgress] = useState<{ [id: string]: number }>({})

  const setFileAttrs = (inputName: string, fileId: string, data: Partial<iUploadFile>) => {
    setFiles((files) => ({
      ...files,
      [inputName]: files[inputName].map((file) => (file.id === fileId ? { ...file, ...data } : file))
    }))
  }
  const addFile = (inputName: string, newFile: iUploadFile) => {
    setFiles((files) => ({ ...files, [inputName]: [...(files[inputName] ?? []), newFile] }))
  }
  const deleteFile = (inputName: string, fileId: string) => {
    setFiles((files) => ({
      ...files,
      [inputName]: files[inputName].filter((file) => (file.id !== fileId))
    }))
  }

  // TODO use callback
  const upload = async (ipnutName: string, file: iUploadFile) => {
    try {
      addFile(ipnutName, file)
      const fileContentData = cipherCert ? await cipherFile(file.fileData, cipherCert) : Buffer.from(await file.fileData.arrayBuffer())

      IenApi.fileUpload(
        extSystemId,
        file.fileData,
        fileContentData,
        (event) => {
          const percent = Math.round((100 * event.loaded) / event.total)
          setProgress((progress) => ({ ...progress, [file.id]: percent }))
        },
        file.signal.signal
      )
        .then((data) => {
          setFileAttrs(ipnutName, file.id, { response: data, uploaded: true })
        })
        .catch((err) => {
          toastError(`Soubor ${file.fileData.name} se nepodařilo nahrát`)
          setFileAttrs(ipnutName, file.id, { error: errorIenProces(err) })
        })

    } catch (e) {
      toastError('Soubor se nepodařilo zašifrovat')
    }
  }

  const cancelFileUploading = (inputName: string, fileId: string) => {
    const file = files[inputName].find((f) => f.id === fileId)
    file?.signal.abort()
    deleteFile(inputName, fileId)
  }

  const deleteFileUploaded = (inputName: string, fileId: string) => {
    const file = files[inputName].find((f) => f.id === fileId)
    if (file?.response?.tmpId) {
      IenApi.fileUploadDelete(extSystemId, file.response.tmpId)
        .catch(errorIenProcesToast)
    }
    deleteFile(inputName, fileId)
  }

  const value = useMemo(() => ({
    uploadFile: (inputName: string, file: File) => {
      const uploadFile: iUploadFile = {
        id: v4(),
        fileData: file,
        signal: new AbortController(),
      }
      upload(inputName, uploadFile)
    },
    cancelFile: cancelFileUploading,
    deleteFile: deleteFileUploaded,
    progress: progress,
    files: files,
    config: config,
    updateConfig: (data: Partial<iUploadFileConfig>) => setConfig(config => ({ ...config, ...data }))
  }), [files, progress, config])

  return (
    <FormUploadContext.Provider value={value}>
      {children}
    </FormUploadContext.Provider>
  )
}

export const withFormUpload = (WrapedComp: any) => (props: any) =>
  <FormUploadProvider>
    <WrapedComp {...props}/>
  </FormUploadProvider>
